import { useEffect, useState } from "react"
import { Modal, Collapse } from "antd"
import { InfoCircleFilled } from "@ant-design/icons"
import { roundOff } from "src/utils/helperFunctions"
import { useIsMobileDevice } from "src/utils/hooks"
import { PLAGIARISM_BREAKDOWN_LABELS } from "src/pages/OnePagerReport/constant"
import { Arrow, CloseIcon } from "src/assets/icons"
import "./styles.scss"

const PlagiarismPill = ({ plagiarismReport, plagiarismConfig, plagiarismChecked }) => {
    const [isPlagiarismVisible, setIsPlagiarismVisible] = useState(false)
    const [isPlagiarismAvailable, setIsPlagiarismAvailable] = useState(false)
    const [isDetailVisible, setIsDetailVisible] = useState(false)
    const [maxSimilarity, setMaxSimilarity] = useState(0)
    const [categoryDetails, setCategoryDetails] = useState({
        background: "#e9e9e9",
        textColor: "#000",
        label: "",
    })
    const isMobile = useIsMobileDevice()

    useEffect(() => {
        if (plagiarismConfig?.plagiarism) {
            setIsPlagiarismVisible(true)
            if (plagiarismChecked && !!plagiarismReport) {
                const { minAllowed, maxAllowed } = plagiarismConfig
                let maxSimilarity = plagiarismReport?.maxProbability
                if (maxSimilarity < 0) {
                    setIsPlagiarismAvailable(false)
                    return
                }
                maxSimilarity = roundOff(maxSimilarity * 100, 1)
                setMaxSimilarity(maxSimilarity)
                const catObj = getCategoryDetails(maxSimilarity, minAllowed, maxAllowed)
                setCategoryDetails(catObj)
                setIsPlagiarismAvailable(true)
            }
        }
    }, [plagiarismConfig, plagiarismReport, plagiarismChecked])

    const getCategoryDetails = (similarity, min, max) => {
        let catObj = {}
        if (similarity >= max) {
            catObj = {
                background: "#FFEFEF",
                textColor: "#EB1A1A",
                label: "High Risk",
            }
        } else if (similarity >= min) {
            catObj = {
                background: "#ffecc5",
                textColor: "#ffb004",
                label: "Medium Risk",
            }
        } else {
            catObj = {
                textColor: "#3d922c",
                background: "#d5ffca",
                label: "Low Risk",
            }
        }
        return catObj
    }

    const getBreakdownText = (score, aspect) => {
        if (score >= 0.9) {
            switch (aspect) {
                case "token":
                    return "The exact structure and words used in code are highly similar."
                case "lexical":
                    return "The naming conventions are largely identical."
                case "ast":
                    return "The flow and structure of the programs are almost identical."
                case "semantic":
                    return "The core logic and behavior of both codes are essentially the same."
                default:
                    return ""
            }
        } else if (score >= 0.75) {
            switch (aspect) {
                case "token":
                    return "Many elements and keywords are similar between codes."
                case "lexical":
                    return "Several naming patterns are shared."
                case "ast":
                    return "Some flow similarities detected."
                case "semantic":
                    return "Some overlap in logic and problem-solving approach."
                default:
                    return ""
            }
        } else if (score >= 0.5) {
            return "Some similarities detected, but substantial differences remain."
        } else {
            return "Minimal similarities observed."
        }
    }

    return (
        <>
            {
                isPlagiarismVisible
                    ? <div
                        style={{
                            background: categoryDetails.background,
                        }}
                        className={`score-pill flex items-center justify-center b-40 py-10 px-20 ${isPlagiarismAvailable ? "cursor-pointer" : ""} select-none`}
                        onClick={isPlagiarismAvailable ? () => setIsDetailVisible(true) : () => { }}
                    >
                        <p
                            className="text-black mr-4"
                        >
                            Plagiarism:
                        </p>
                        <p
                            style={{
                                color: categoryDetails.textColor,
                            }}
                            className="mr-8"
                        >
                            {
                                isPlagiarismAvailable
                                    ? `${maxSimilarity}% ${categoryDetails.label}`
                                    : "Not Available"
                            }
                        </p>
                        {
                            isPlagiarismAvailable
                                ? <InfoCircleFilled style={{ color: "#000" }} />
                                : <></>
                        }
                    </div> : <></>
            }
            <Modal
                width={isMobile ? "90vw" : "55vw"}
                closeIcon={<CloseIcon />}
                footer={null}
                open={isDetailVisible}
                onCancel={() => setIsDetailVisible(false)}
                className="plagiarism-detail-modal select-none"
                wrapClassName="plagiarism-detail-wrap"
                centered={true}
                title={<p className="text-24 weight-700 color-black capitalize">plagiarism check</p>}
            >
                <div
                    className="flex flex-col items-start justify-center py-20 px-20 b-20"
                    style={{
                        background: categoryDetails.background
                    }}
                >
                    <p
                        style={{
                            color: categoryDetails.textColor,
                        }}
                        className="text-32 weight-600 mb-2"
                    >
                        {maxSimilarity}%{" "}
                        <span className="text-22 weight-600">
                            {categoryDetails.label}
                        </span>
                    </p>
                    <p
                        className="text-16 weight-400 color-black"
                    >
                        Compared against {plagiarismReport?.comparisonCount ?? ""} other submissions.
                    </p>
                </div>
                <p className="color-black capitalize text-20 weight-700 mt-20 mb-10">top matching submissions:</p>
                <Collapse
                    destroyInactivePanel={false}
                    expandIcon={(props) => {
                        return (<Arrow color={"#000"} />)
                    }}
                >
                    {
                        plagiarismReport?.individualProbabilities?.length ?
                            plagiarismReport?.individualProbabilities
                                ?.sort((a, b) => b?.metadata?.overall_similarity - a?.metadata?.overall_similarity)
                                ?.map(individual => (
                                    <Collapse.Panel
                                        key={individual?.linkId}
                                        header={
                                            <div className="flex flex-col items-start">
                                                <div className={`flex ${isMobile ? "flex-col items-start mb-12" : "items-center mb-4"}`}>
                                                    <p className="text-16 color-black weight-600 mr-10 break-word">{individual?.userData?.name}</p>
                                                    <p
                                                        className="text-16 weight-600"
                                                        style={{
                                                            color: getCategoryDetails(individual?.metadata?.overall_similarity * 100, plagiarismConfig.minAllowed, plagiarismConfig.maxAllowed).textColor
                                                        }}
                                                    >{roundOff(individual?.metadata?.overall_similarity * 100, 1)}% match</p>
                                                </div>
                                                <div className={`flex ${isMobile ? "flex-col items-start" : "items-center"}`}>
                                                    <p className="color-black text-14 weight-400 opacity-8 break-word mr-14">{individual?.userData?.email}</p>
                                                    {
                                                        individual?.linkId && individual?.userData?.userId
                                                            ? <a
                                                                onClick={(e) => e.stopPropagation()}
                                                                href={`/report/v2/link/${individual?.linkId}/users/${individual?.userData?.userId}`}
                                                                target="_blank"
                                                                className="report-link text-14 weight-700"
                                                            >
                                                                View Full Report <Arrow />
                                                            </a> : <></>
                                                    }
                                                </div>
                                            </div>
                                        }
                                    >
                                        <p className="text-12 weight-700 color-black opacity-8 uppercase mb-12">similarity breakdown with {individual?.userData?.name}:</p>

                                        {
                                            Object.keys(individual?.metadata?.traditional_methods)?.map(category => (
                                                <div key={category} className="flex items-start breakdown-point mb-4">
                                                    <p className="text-14 weight-400 color-black mr-8">✤</p>
                                                    <p className="text-14 weight-400 color-black"><strong>{PLAGIARISM_BREAKDOWN_LABELS[category.split("_")[1]]}:</strong>{" "}{getBreakdownText(individual?.metadata?.traditional_methods[category], category.split("_")[1])}</p>
                                                </div>
                                            ))
                                        }
                                    </Collapse.Panel>

                                )) : <></>
                    }
                </Collapse>

            </Modal>
        </>
    )
}

export default PlagiarismPill
